import mapKnownValues from "../utils/mapKnownValues";
import AggregatorLink from "./aggregatorLink";
import { AssetAllocationClass, ReallocationChoice, ReallocationData, ReallocationSurveyData } from "./reallocation";
import { UNKNOWN } from "./transferSource";

export type ReallocationResponseData = {
  age: Record<string, number>;
  destinationAccountId: string;
  financialEducation: string;
  reallocationId: number;
  equityAllocation: number;
  // TODO: complete this
};

const transformReallocationChoice = (equityAllocation: number | null): AssetAllocationClass | undefined => {
  switch (equityAllocation) {
    case 95:
      return AssetAllocationClass.NINETYFIVE;
    case 85:
      return AssetAllocationClass.EIGHTYFIVE;
    case 75:
      return AssetAllocationClass.SEVENTYFIVE;
    case 65:
      return AssetAllocationClass.SIXTYFIVE;
    case 55:
      return AssetAllocationClass.FIFTYFIVE;
    case 45:
      return AssetAllocationClass.FORTYFIVE;
    case 35:
      return AssetAllocationClass.THIRTYFIVE;
    case 25:
      return AssetAllocationClass.TWENTYFIVE;
    default:
      return undefined;
  }
};

export type DestinationTransferStep = "roth" | "source" | "destination" | "userDestination";
export type TransferDestinationPlanType = "BOTH" | "ROTH" | "TRADITIONAL" | "OTHER";

export enum DestinationType {
  traditionalDestination = "DESTINATION",
  rothDestination = "ROTH_DESTINATION",
}

export enum AccountType {
  employerSponsored = "EMPLOYER_SPONSORED",
  ira = "IRA",
}

export type IRAAccountOpeningData = {
  iraAccountOpeningDataId: number;
  accountOpeningDataJson: string;
  status: string;
};

export type TransferDestinationResponse = {
  aggregatorLink?: AggregatorLink;
  transferId: number;
  destinationType: DestinationType;
  destinationAccountType: AccountType;
  destinationAccountId: number;
  planSponsor: string | null;
  providerName: string | null;
  planName: string | null;
  planNumber: string | null;
  planType: TransferDestinationPlanType;
  statementSkipped: boolean;
  aggregatorSkipped: boolean;
  aggregatorUpdateRequired: boolean; // if false, we need to launch into update mode
  traditionalIraAccountNumber: string | null;
  rothIraAccountNumber: string | null;
  yetToOpen: boolean;
  accountOpeningUrl: string | null;
  destinationStatementFileNames: string[];
  reallocation: ReallocationResponseData | null;
  skipReallocation: null | boolean;
};

export default class TransferDestination {
  transferId = -1;
  destinationAccountId = -1;
  destinationType: DestinationType = DestinationType.traditionalDestination;
  destinationAccountType: AccountType = AccountType.employerSponsored;
  planSponsor = ""; // Only if employer account
  providerName = "";
  providerSkipped: boolean | null = null;
  planName = "";
  planNumber = "";
  planType: TransferDestinationPlanType | "" = ""; // also aka Account Type
  statementSkipped: boolean | null = null;
  destinationStatementFileNames: string[] = [];
  aggregatorSkipped: null | boolean = null;
  aggregatorPublicToken: null | string = null; // From plaid
  aggregatorLink: AggregatorLink | undefined = undefined;

  // IRA Specific Fields
  traditionalIraAccountNumber = "";
  rothIraAccountNumber = "";
  yetToOpen = false; // If we have this set to true, we must have an accountOpeningURL as well
  accountOpeningUrl = "";

  // Manifest's IRA partner
  isManifestIRAPartner = false;
  iraAccountOpeningData: IRAAccountOpeningData | null = null;

  // Misc
  accountUsedForAllExistingTransfers = true; // enabled by default on purpose

  // Reallocation
  shouldReallocation = false;
  reallocationData: ReallocationData | null = null;
  reallocationId: number | null = null;
  reallocationSelection: any | null = null;
  reallocationChoice: string | null = null;
  skipReallocation: boolean | null = null;
  destinationInstructionId: string | null = null;

  constructor(params: Partial<TransferDestination> = {}) {
    mapKnownValues(this, params);
  }

  toJSON() {
    const statementSkipped = this.statementSkipped === null ? true : this.statementSkipped;

    // For the backend, any empty string must be converted to null
    const data = {
      ...this,
      statementSkipped,
      destinationAccountId: this.destinationAccountId === -1 ? null : this.destinationAccountId,
      aggregatorSkipped: this.aggregatorSkipped === null || this.aggregatorSkipped, // Must be a boolean for the backend
      planSponsor: this.planSponsor || null,
      providerName: this.providerName || null,
      providerSkipped: !this.providerName,
      planName: this.planName || null,
      planNumber: this.planNumber || null,
      planType: this.planType || null,
      traditionalIraAccountNumber: this.traditionalIraAccountNumber || null,
      rothIraAccountNumber: this.rothIraAccountNumber || null,
      accountOpeningUrl: this.accountOpeningUrl || null,
      missingInformation: undefined,
    } as Record<keyof TransferDestination, any>;

    if (statementSkipped) {
      data.destinationStatementFileNames = [];
    } else {
      data.traditionalIraAccountNumber = null;
      data.rothIraAccountNumber = null;
    }

    // IRA DestinationAccountDetails
    if (this.destinationAccountType === AccountType.ira) {
      const iraData = {
        ...data,
        planName: undefined,
        planNumber: undefined,
        planSponsor: undefined,
        planType: undefined,
      };

      // If we have an account number, we must make sure to not send a yetToOpen
      if (this.traditionalIraAccountNumber || this.rothIraAccountNumber) {
        return {
          ...iraData,
          yetToOpen: false,
        };
      }
      // If we are opening a new account, we shouldn't send the traditional IRA Account number
      if (this.accountOpeningUrl) {
        return {
          ...iraData,
          traditionalIraAccountNumber: null,
          rothIraAccountNumber: null,
        };
      }
      return iraData;
    }

    // Employer Sponsored DestinationAccountDetails
    return {
      ...data,
      traditionalIraAccountNumber: undefined,
      rothIraAccountNumber: undefined,
      yetToOpen: undefined,
      accountOpeningUrl: undefined,
    };
  }

  get planSponsorDisplay() {
    if (this.yetToOpen) {
      return "New IRA (pending)";
    }
    return this.planSponsor || "Individual Retirement Account";
  }

  addFileNameToStatements(fileName: string) {
    if (this.destinationStatementFileNames.includes(fileName)) return;
    this.destinationStatementFileNames.push(fileName);
  }

  removeFileNameFromStatements(fileName: string) {
    const indexOfFileNameFound = this.destinationStatementFileNames.indexOf(fileName);
    if (indexOfFileNameFound === -1) return;
    this.destinationStatementFileNames.splice(indexOfFileNameFound, 1);
  }

  updateReallocationData(updatedReallocationData: Partial<ReallocationData>) {
    this.reallocationData = {
      ...this.reallocationData,
      ...updatedReallocationData,
    };
    return this.reallocationData;
  }

  static fromApi(response: TransferDestinationResponse) {
    // We must convert null to the type we understand
    const newResponse: Partial<TransferDestination> = {
      ...response,
      aggregatorLink: response.aggregatorLink ? new AggregatorLink(response.aggregatorLink) : undefined,
      planSponsor: response.planSponsor || undefined,
      providerName: response.providerName || undefined,
      planName: response.planName || undefined,
      planNumber: response.planNumber || undefined,
      planType: response.planType || undefined,
      traditionalIraAccountNumber: response.traditionalIraAccountNumber || undefined,
      rothIraAccountNumber: response.rothIraAccountNumber || undefined,
      accountOpeningUrl: response.accountOpeningUrl || undefined,
      reallocationId: response.reallocation?.reallocationId || null,
      skipReallocation: response.skipReallocation,
      isManifestIRAPartner: response.providerName === "Futuremoney" || response.providerName === "Betterment", // IRA PARTNERSHIP || BETTERMENT INTEGRATION ENABLEMENT
      reallocationData: {
        reallocationSurvey: response.reallocation as ReallocationSurveyData,
        reallocationChoice: response.skipReallocation
          ? ReallocationChoice.LEAVEASIS
          : response.skipReallocation === false
          ? ReallocationChoice.REALLOCATE
          : response.reallocation?.reallocationId !== null && response.reallocation?.reallocationId !== undefined
          ? ReallocationChoice.REALLOCATE
          : UNKNOWN,
        reallocationSelection: transformReallocationChoice(response.reallocation?.equityAllocation || null),
      },
    };
    return new TransferDestination(newResponse);
  }
}
